import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { getCardSignals } from "../store/collection-store";

/**
 * Image switcher for collection product cards.
 * Wraps the image containers and reactively shows/hides variant images
 * based on the selected variant from the collection store.
 *
 * Usage:
 * <card-image-switcher data-card-id="p-12345">
 *   <div class="media--product">...</div>
 *   <div class="media--variants">...</div>
 * </card-image-switcher>
 */
@customElement("card-image-switcher")
export class CardImageSwitcher extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";

  private productImagesWrapper: HTMLElement | null = null;
  private variantImagesWrapper: HTMLElement | null = null;
  private variantImages: HTMLImageElement[] = [];

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._findImageElements();

    // Subscribe to variant changes AFTER elements are found
    effect(() => {
      if (!this.cardId) return;

      const signals = getCardSignals(this.cardId);
      if (!signals) return;

      const selectedVariant = signals.selectedVariant.value;
      if (selectedVariant) {
        this._switchImage(selectedVariant.id);
      }
    });
  }

  private _findImageElements() {
    // Find the image container elements
    this.productImagesWrapper = this.querySelector(".media--product");
    this.variantImagesWrapper = this.querySelector(".media--variants");
    this.variantImages = Array.from(
      this.variantImagesWrapper?.querySelectorAll("img[data-variant-id]") ?? []
    );
  }

  private _switchImage(variantId: number) {
    if (!this.productImagesWrapper || !this.variantImagesWrapper) {
      console.warn(`card-image-switcher [${this.cardId}]: DOM elements not found`);
      return;
    }

    const variantIdStr = variantId.toString();
    // console.log(`card-image-switcher [${this.cardId}]: switching to variant ${variantIdStr}`);

    // Find the variant image matching the selected variant
    const selectedVariantImage = this.variantImages.find((image) => {
      const datasetVariantId = image.dataset.variantId;
      if (!datasetVariantId) {
        console.error("datasetVariantId not found");
        return false;
      }
      return datasetVariantId === variantIdStr;
    });

    if (selectedVariantImage) {
      // Hide all variant images first
      for (const image of this.variantImages) {
        (image as HTMLElement).style.display = "none";
      }

      // Show variant images wrapper, hide product images
      this.productImagesWrapper.style.display = "none";
      this.variantImagesWrapper.style.display = "block";
      selectedVariantImage.style.display = "block";
      selectedVariantImage.style.opacity = "1";
    } else {
      // No variant-specific image found, show default product images
      this.variantImagesWrapper.style.display = "none";
      this.productImagesWrapper.style.display = "block";
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}
